import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { isSignupEmailTakenError } from '../../util/errors';
import {
  Page,
  NamedLink,
  NamedRedirect,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  Modal,
  TermsOfService,
} from '../../components';
import { LoginForm, SignupForm } from '../../forms';
import { TopbarContainer } from '../../containers';
import { login, authenticationInProgress, signup } from '../../ducks/Auth.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';

import css from './AuthenticationPage.css';

// A company outside Finland onboards Stripe Connect and sells directly
// (spec decision 2), so it signs up as a freelancer; the flag keeps the
// "company abroad" wording in account settings.
export const accountRoleForSignup = accountRole => {
  const isCompanyAbroad = accountRole === 'company-abroad';
  return { account_role: isCompanyAbroad ? 'freelancer' : accountRole, isCompanyAbroad };
};

export class AuthenticationPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tosModalOpen: false,
    };
  }

  render() {
    const {
      authInProgress,
      currentUser,
      intl,
      isAuthenticated,
      location,
      loginError,
      scrollingDisabled,
      signupError,
      submitLogin,
      submitSignup,
      tab,
      onManageDisableScrolling,
      isOpen,
      onClose,
    } = this.props;

    const isLogin = tab === 'login';
    const isSignup = tab === 'signup';
    const locationFrom = location.state && location.state.from ? location.state.from : null;
    const from = locationFrom || null;

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!user.id;

    // Already authenticated, redirect away from auth page
    if (isAuthenticated && from) {
      return <Redirect to={from} />;
    } else if (isAuthenticated && currentUserLoaded) {
      return <NamedRedirect name="DashboardPage" />;
    }

    const loginErrorMessage = (
      <div className={css.error}>
        <FormattedMessage id="AuthenticationPage.loginFailed" />
      </div>
    );

    const signupErrorMessage = (
      <div className={css.error}>
        {isSignupEmailTakenError(signupError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    );

    const errorMessage = (error, message) => (error ? message : null);
    const loginOrSignupError = isLogin
      ? errorMessage(loginError, loginErrorMessage)
      : errorMessage(signupError, signupErrorMessage);

    const handleSubmitSignup = values => {
      const {
        fname,
        lname,
        accountRole,
        phoneNumber,
        howDidYouHear,
        howDidYouHearOther,
        ...rest
      } = values;

      const { account_role, isCompanyAbroad } = accountRoleForSignup(accountRole);

      const params = {
        firstName: fname.trim(),
        lastName: lname.trim(),
        account_role,
        isCompanyAbroad,
        phoneNumber: '+' + phoneNumber,
        howDidYouHear,
        // The free-text detail is only meaningful when "other" was selected;
        // the field keeps its value in form state if the user switches away.
        ...(howDidYouHear === 'other' && howDidYouHearOther ? { howDidYouHearOther } : {}),
        ...rest,
      };

      submitSignup(params);
    };

    const headerLogin = (
      <h1 className={css.tab}>
        <FormattedMessage id="AuthenticationPage.loginLinkText" />
      </h1>
    );

    const authenticationForms = isLogin ? (
      <div className={css.content}>
        {headerLogin}
        {loginOrSignupError}
        <LoginForm className={css.form} onSubmit={submitLogin} inProgress={authInProgress} />
        <div>
          <div className={css.socialButtonsOr}>
            <span className={css.socialButtonsOrText}>
              <FormattedMessage id="AuthenticationPage.notSignedYet" />
            </span>
          </div>
          <NamedLink name="SignupPage" className={css.signupLink}>
            <FormattedMessage id="TopbarDesktop.signup" />
          </NamedLink>
        </div>
      </div>
    ) : (
      <div>
        <SignupForm
          className={css.form}
          isOpen={isOpen}
          onClose={onClose}
          onManageDisableScrolling={onManageDisableScrolling}
          onSubmit={handleSubmitSignup}
          inProgress={authInProgress}
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          signupError={loginOrSignupError}
        />
      </div>
    );

    const siteTitle = config.siteTitle;
    const schemaTitle = isLogin
      ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
      : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });

    if (isSignup) {
      return (
        <div>
          {authenticationForms}
          <Modal
            id="AuthenticationPage.tos"
            isOpen={this.state.tosModalOpen}
            onClose={() => this.setState({ tosModalOpen: false })}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.termsWrapper}>
              <h2 className={css.termsHeading}>
                <FormattedMessage id="AuthenticationPage.termsHeading" />
              </h2>
              <TermsOfService />
            </div>
          </Modal>
        </div>
      );
    } else {
      return (
        <Page
          title={schemaTitle}
          scrollingDisabled={scrollingDisabled}
          schema={{
            '@context': 'http://schema.org',
            '@type': 'WebPage',
            name: schemaTitle,
          }}
        >
          <LayoutSingleColumn>
            <LayoutWrapperTopbar>
              <TopbarContainer currentPage="AuthenticationPage" authenticationPageTopBar={true} />
            </LayoutWrapperTopbar>
            <LayoutWrapperMain className={css.layoutWrapperMain}>
              <div className={css.root}>{authenticationForms}</div>
              <Modal
                id="AuthenticationPage.tos"
                isOpen={this.state.tosModalOpen}
                onClose={() => this.setState({ tosModalOpen: false })}
                usePortal
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <div className={css.termsWrapper}>
                  <h2 className={css.termsHeading}>
                    <FormattedMessage id="AuthenticationPage.termsHeading" />
                  </h2>
                  <TermsOfService />
                </div>
              </Modal>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    }
  }
}

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  tab: 'signup',
};

const { bool, func, object, oneOf, shape } = PropTypes;

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup']),
  onManageDisableScrolling: func.isRequired,
  location: shape({ state: object }).isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError } = state.Auth;
  const { currentUser } = state.user;

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
